import { createSignal, type Accessor } from 'solid-js'
import { api, currentProjectId } from './api/client.ts'
import { project, saveNow } from './project.ts'
import { newId } from './scene/factory.ts'
import type { Scene } from './scene/types.ts'

/**
 * Pasted images are placed before they are stored, under a local id that
 * renders from a blob URL. The scene keeps the local id for the session — undo
 * history holds it too — and it is mapped to the stored id only on save.
 */
interface LocalAsset {
  url: string
  storedId: string | null
}

const PREFIX = 'local:'
const RETRY_MIN_MS = 1000
const RETRY_MAX_MS = 30_000

const local = new Map<string, LocalAsset>()

export function isLocalAsset(assetId: string): boolean {
  return local.has(assetId)
}

/** Object URLs for stored assets, by project and asset id, filled in once read. */
const stored = new Map<string, Accessor<string>>()

/** URL to display an asset; empty until a stored one has been read. Reactive. */
export function assetSrc(assetId: string): string {
  const pending = local.get(assetId)
  if (pending) return pending.url
  const projectId = currentProjectId()
  if (!projectId) return ''
  const key = `${projectId}/${assetId}`
  let url = stored.get(key)
  if (!url) {
    const [get, set] = createSignal('')
    stored.set(key, get)
    void api.asset(projectId, assetId).then((blob) => blob && set(URL.createObjectURL(blob)))
    url = get
  }
  return url()
}

export async function assetBlob(assetId: string): Promise<Blob> {
  const pending = local.get(assetId)
  if (pending) return await (await fetch(pending.url)).blob()
  const projectId = currentProjectId()
  const blob = projectId ? await api.asset(projectId, assetId) : undefined
  if (!blob) throw new Error(`Could not read asset ${assetId}`)
  return blob
}

/** Register a blob for immediate display and start uploading it. */
export function addLocalAsset(file: File, ensureProjectId: () => Promise<string>): string {
  const id = PREFIX + newId()
  const entry: LocalAsset = { url: URL.createObjectURL(file), storedId: null }
  local.set(id, entry)
  void upload(entry, file, ensureProjectId)
  return id
}

async function upload(
  entry: LocalAsset,
  file: File,
  ensureProjectId: () => Promise<string>,
): Promise<void> {
  // Pinned once known so a retry after switching projects cannot land the
  // asset in the wrong one.
  let projectId = project()?.id ?? null
  let delay = RETRY_MIN_MS
  for (;;) {
    try {
      projectId ??= await ensureProjectId()
      const info = await api.uploadAsset(projectId, file, file.name || 'image.png')
      entry.storedId = info.assetId
      if (project()?.id === projectId) void saveNow()
      return
    } catch (err) {
      console.warn('Storing asset failed, retrying', err)
      await new Promise((r) => setTimeout(r, delay))
      delay = Math.min(delay * 2, RETRY_MAX_MS)
    }
  }
}

/** The scene with local ids swapped for stored ids, or null while any are still being stored. */
export function resolveLocalAssets(scene: Scene): Scene | null {
  for (const node of Object.values(scene.nodes)) {
    if (node.type !== 'image' || !node.assetId.startsWith(PREFIX)) continue
    const storedId = local.get(node.assetId)?.storedId
    if (!storedId) return null
    node.assetId = storedId
  }
  return scene
}
